import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { track } from "../../lib/analytics";
import { Activity, Copy, MessageSquare, Plus, Swords, Trash2 } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@v2/components/ui/tabs";
import { supabaseV2 } from "../../lib/supabase";
import { formatDateTime } from "../../lib/format";
import { usePermissions } from "../../lib/permissions";
import { seasonErrorMessage, useSeason } from "../../lib/seasons";
import {
  createPublicToken,
  createSessionToken,
  sessionPublicUrl,
} from "../../lib/sessions";
import { useTeamOpponents, useTeamCompetitions } from "../../lib/team-config";
import { TeamSuggestions } from "../../components/team-suggestions";
import { CompetitionPicker } from "../../components/competition-picker";
import type { Session } from "../../types";
import { EventsView } from "./EventsView";
import { MatchDaysView } from "./MatchDaysView";

export function SessionsView({ teamId }: { teamId: string }) {
  const { t } = useTranslation("sessions");
  const { canManage } = usePermissions();
  // Sulle stagioni chiuse non si creano sessioni: il banner in cima spiega il perche'.
  const { isViewingClosed } = useSeason();
  const [tab, setTab] = useState<"training" | "matches">("training");
  const [wizardOpen, setWizardOpen] = useState(false);
  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={tab} onValueChange={(v) => { track.tabChange("sessions", v); setTab(v as typeof tab); }} className="w-full sm:w-auto">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="training" className="flex-1 sm:flex-none">{t("events.tab")}</TabsTrigger>
            <TabsTrigger value="matches" className="flex-1 sm:flex-none">{t("matchdays.tab")}</TabsTrigger>
          </TabsList>
        </Tabs>
        {canManage && (
          <Button
            variant="default"
            disabled={isViewingClosed}
            title={isViewingClosed ? t("header.closedSeasonHint") : undefined}
            onClick={() => { track.ctaClick("new_session", { team_id: teamId }); setWizardOpen(true); }}
            className="w-full sm:w-auto"
          >
            <Plus />
            {t("header.newSession")}
          </Button>
        )}
      </div>

      {tab === "training" ? (
        <EventsView teamId={teamId} />
      ) : (
        <MatchDaysView teamId={teamId} />
      )}
      {canManage && (
        <NewSessionWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          teamId={teamId}
          onCreated={(hasMatches) => setTab(hasMatches ? "matches" : "training")}
        />
      )}
    </div>
  );
}

type WizardMatchRow = {
  opponent_name: string;
  competition_name: string;
  date: string;
  time: string;
  home_away: "home" | "away" | "neutral";
};

const emptyWizardMatchRow = (date: string): WizardMatchRow => ({
  opponent_name: "",
  competition_name: "",
  date,
  time: "",
  home_away: "neutral",
});

function NewSessionWizard({
  open,
  onOpenChange,
  teamId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  onCreated: (hasMatches: boolean) => void;
}) {
  const { t } = useTranslation("sessions");
  const queryClient = useQueryClient();
  const opponentsQuery = useTeamOpponents(teamId);
  const competitionsQuery = useTeamCompetitions(teamId);
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("");
  const tomorrowIso = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };
  const [date, setDate] = useState(() => tomorrowIso());
  const [time, setTime] = useState("21:30");
  const [deadlineDate, setDeadlineDate] = useState(() => tomorrowIso());
  const [deadlineTime, setDeadlineTime] = useState("18:00");
  const [deadlineDirty, setDeadlineDirty] = useState(false);
  const [location, setLocation] = useState("");
  const [hasMatches, setHasMatches] = useState(false);
  const [includeGuests, setIncludeGuests] = useState(false);
  const [matchRows, setMatchRows] = useState<WizardMatchRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [shareEvent, setShareEvent] = useState<Session | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    const tomorrow = tomorrowIso();
    setTitle("");
    setDate(tomorrow);
    setTime("21:30");
    setDeadlineDate(tomorrow);
    setDeadlineTime("18:00");
    setDeadlineDirty(false);
    setLocation("");
    setHasMatches(false);
    setIncludeGuests(false);
    setMatchRows([emptyWizardMatchRow(tomorrow)]);
    setError(null);
    setShareLink(null);
    setShareEvent(null);
  }, [open]);

  // Sincronizza deadline alla data evento se l'utente non l'ha toccata
  useEffect(() => {
    if (!deadlineDirty) {
      setDeadlineDate(date);
      setDeadlineTime("18:00");
    }
  }, [date, deadlineDirty]);

  useEffect(() => {
    if (matchRows.length === 0) {
      setMatchRows([emptyWizardMatchRow(date)]);
    } else {
      setMatchRows((rows) => rows.map((r) => (r.date ? r : { ...r, date })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const updateMatchRow = (idx: number, patch: Partial<WizardMatchRow>) => {
    setMatchRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addMatchRow = () => setMatchRows((rows) => [...rows, emptyWizardMatchRow(date)]);
  const removeMatchRow = (idx: number) => setMatchRows((rows) => rows.filter((_, i) => i !== idx));

  const validMatchRows = hasMatches ? matchRows.filter((r) => r.opponent_name.trim()) : [];

  const create = useMutation({
    mutationFn: async () => {
      const startsAt = new Date(`${date}T${time || "21:00"}`).toISOString();
      const deadline = deadlineDate
        ? new Date(`${deadlineDate}T${deadlineTime || time || "21:00"}`).toISOString()
        : startsAt;
      const defaultTitle = title.trim() || (hasMatches ? `Giornata partite ${date}` : `Allenamento ${date}`);
      const { data: authData } = await supabaseV2.auth.getUser();
      const { data: sessionData, error: sessionError } = await supabaseV2
        .from("sessions")
        .insert({
          team_id: teamId,
          title: defaultTitle,
          starts_at: startsAt,
          response_deadline_at: deadline,
          location: location.trim() || null,
          status: "scheduled",
          // has_matches gia' true al primo INSERT cosi' il trigger
          // notify_session_published manda subito "Nuova partita" invece di
          // "Nuovo allenamento". Verra' comunque ricalcolato dal trigger
          // refresh_session_has_matches dopo l'INSERT dei matches.
          has_matches: hasMatches,
          include_guests: includeGuests,
          created_by: authData.user?.id,
        })
        .select("*")
        .single();
      if (sessionError) throw sessionError;
      const created = sessionData as Session;
      if (hasMatches && validMatchRows.length > 0) {
        const inserts = validMatchRows.map((r, idx) => ({
          session_id: created.id,
          team_id: teamId,
          opponent_id: null,
          competition_id: null,
          starts_at: r.date ? new Date(`${r.date}T${r.time || time || "21:00"}`).toISOString() : startsAt,
          opponent_name: r.opponent_name.trim(),
          home_away: r.home_away,
          competition_name: r.competition_name.trim() || null,
          our_score: null,
          opponent_score: null,
          result_notes: null,
          sort_order: idx,
        }));
        const { error: matchesError } = await supabaseV2.from("matches").insert(inserts);
        if (matchesError) throw matchesError;
      }
      return created;
    },
    onSuccess: async (created) => {
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "all"] });
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "training"] });
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "match_day"] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["matches", teamId, "home"] });
      queryClient.invalidateQueries({ queryKey: ["seasons", teamId] });
      // GA4 event "session_created" — engagement event del wizard sessione.
      track.sessionCreated({
        team_id: teamId,
        session_id: created.id,
        has_matches: hasMatches,
        include_guests: includeGuests,
      });
      onCreated(hasMatches);
      // Genera link condivisibile
      try {
        const token = createPublicToken();
        await createSessionToken(created, token);
        setShareLink(sessionPublicUrl(token));
        setShareEvent(created);
        setStep(3);
      } catch (e: any) {
        setError(`Sessione creata, ma errore generando link: ${e.message || e}`);
        setStep(3);
      }
    },
    onError: (e: any) => {
      setError(seasonErrorMessage(e) || e.message || t("wizard.error"));
    },
  });

  const canSubmit = !create.isPending && (!hasMatches || validMatchRows.length > 0);

  const copyShareLink = async () => {
    if (!shareLink) return;
    await navigator.clipboard.writeText(shareLink);
  };
  const copyWhatsApp = async () => {
    if (!shareLink || !shareEvent) return;
    const text = [
      t("wizard.share.msgPresence", { title: shareEvent.title }),
      shareEvent.starts_at ? formatDateTime(shareEvent.starts_at) : "",
      shareEvent.location ? t("wizard.share.msgLocation", { location: shareEvent.location }) : "",
      t("wizard.share.msgLink", { link: shareLink }),
    ]
      .filter(Boolean)
      .join("\n");
    await navigator.clipboard.writeText(text);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <TeamSuggestions
          opponents={opponentsQuery.data || []}
          competitions={competitionsQuery.data || []}
        />
        <DialogHeader>
          <DialogTitle>
            {step === 3
              ? t("wizard.created")
              : step === 0
                ? t("wizard.newSession")
                : hasMatches
                  ? t("wizard.newMatchDay")
                  : t("wizard.newTraining")}
          </DialogTitle>
          <DialogDescription>
            {step === 0 && t("wizard.step0")}
            {step === 1 && (hasMatches ? t("wizard.step1Match") : t("wizard.step1Training"))}
            {step === 2 && t("wizard.step2")}
            {step === 3 && t("wizard.step3")}
          </DialogDescription>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-3 pt-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setHasMatches(false);
                  setStep(1);
                }}
                className="rounded-2xl bg-raised p-4 text-left hover:bg-[hsl(var(--active))] transition-colors flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold">{t("wizard.training")}</span>
                </div>
                <p className="text-[13px] text-muted-foreground">
                  {t("wizard.trainingDesc")}
                </p>
              </button>
              <button
                type="button"
                onClick={() => {
                  setHasMatches(true);
                  setStep(1);
                }}
                className="rounded-2xl bg-raised p-4 text-left hover:bg-[hsl(var(--active))] transition-colors flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  <Swords className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold">{t("wizard.matchDay")}</span>
                </div>
                <p className="text-[13px] text-muted-foreground">
                  {t("wizard.matchDayDesc")}
                </p>
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("wizard.cancel")}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("wizard.title")}</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={hasMatches ? t("wizard.matchDayTitle", { date }) : t("wizard.trainingTitle", { date })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("wizard.date")}</Label>
                <Input value={date} onChange={(e) => setDate(e.target.value)} type="date" required />
              </div>
              <div className="space-y-1.5">
                <Label>{t("wizard.time")}</Label>
                <Input value={time} onChange={(e) => setTime(e.target.value)} type="time" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("wizard.location")}</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t("wizard.locationPlaceholder")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("wizard.deadlineDate")}</Label>
                <Input
                  value={deadlineDate}
                  onChange={(e) => {
                    setDeadlineDate(e.target.value);
                    setDeadlineDirty(true);
                  }}
                  type="date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("wizard.deadlineTime")}</Label>
                <Input
                  value={deadlineTime}
                  onChange={(e) => {
                    setDeadlineTime(e.target.value);
                    setDeadlineDirty(true);
                  }}
                  type="time"
                />
              </div>
            </div>
            {!deadlineDirty && (
              <p className="text-[13px] text-[hsl(var(--faint))]">
                {t("wizard.deadlineDefault")}
              </p>
            )}
            <label className="flex items-start gap-3 rounded-2xl bg-raised px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeGuests}
                onChange={(e) => setIncludeGuests(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded accent-primary"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium leading-tight">{t("wizard.includeGuests")}</p>
                <p className="text-[13px] text-muted-foreground leading-snug mt-0.5">
                  {t("wizard.includeGuestsHint")}
                </p>
              </div>
            </label>
            {error && !hasMatches && (
              <div className="rounded-2xl px-4 py-3 text-sm bg-[hsl(var(--danger-soft))] text-foreground">
                {error}
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(0)}>
                {t("wizard.back")}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("wizard.cancel")}
              </Button>
              <Button
                variant="default"
                onClick={() => (hasMatches ? setStep(2) : create.mutate())}
                disabled={!date || (!hasMatches && create.isPending)}
              >
                {hasMatches ? t("wizard.next") : create.isPending ? t("wizard.creating") : t("wizard.createTraining")}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            {hasMatches ? (
              <>
                <div className="space-y-2.5 max-h-[50vh] overflow-y-auto pr-1">
                  <div className="hidden sm:grid grid-cols-12 gap-2 text-[13px] text-[hsl(var(--faint))] px-1">
                    <div className="col-span-4">{t("wizard.matches.opponent")}</div>
                    <div className="col-span-3">{t("wizard.matches.competition")}</div>
                    <div className="col-span-2">{t("wizard.matches.time")}</div>
                    <div className="col-span-2">{t("wizard.matches.homeAway")}</div>
                    <div className="col-span-1" />
                  </div>
                  {matchRows.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_1fr_44px] sm:grid-cols-12 gap-2 items-center border-t first:border-t-0 pt-2.5 sm:border-t-0 sm:pt-0">
                      <Input
                        className="col-span-3 sm:col-span-4"
                        value={row.opponent_name}
                        onChange={(e) => updateMatchRow(idx, { opponent_name: e.target.value })}
                        placeholder={t("wizard.matches.opponent")}
                        list="team-opponents"
                      />
                      <CompetitionPicker
                        teamId={teamId}
                        className="col-span-3"
                        value={row.competition_name}
                        onChange={(v) => updateMatchRow(idx, { competition_name: v })}
                      />
                      <Input
                        className="sm:col-span-2"
                        type="time"
                        value={row.time}
                        onChange={(e) => updateMatchRow(idx, { time: e.target.value })}
                      />
                      <Select
                        value={row.home_away}
                        onValueChange={(v) => updateMatchRow(idx, { home_away: v as WizardMatchRow["home_away"] })}
                      >
                        <SelectTrigger className="sm:col-span-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="home">{t("wizard.matches.home")}</SelectItem>
                          <SelectItem value="away">{t("wizard.matches.away")}</SelectItem>
                          <SelectItem value="neutral">{t("wizard.matches.neutral")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="sm:col-span-1 h-11 w-11 text-[hsl(var(--danger))]"
                        onClick={() => removeMatchRow(idx)}
                        disabled={matchRows.length <= 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addMatchRow}>
                  <Plus className="h-4 w-4" />
                  {t("wizard.matches.addMatch")}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {validMatchRows.length === 1
                    ? t("wizard.matches.readyOne", { count: validMatchRows.length })
                    : t("wizard.matches.readyMany", { count: validMatchRows.length })}
                  {" · "}
                  {t("wizard.matches.empty", { count: matchRows.length - validMatchRows.length })}
                </p>
              </>
            ) : (
              <div className="rounded-2xl bg-raised p-4 space-y-1">
                <p className="font-medium">{t("wizard.matches.trainingFallbackTitle")}</p>
                <p className="text-[13px] text-muted-foreground">
                  {t("wizard.matches.trainingFallbackDesc")}
                </p>
              </div>
            )}
            {error && (
              <div className="rounded-2xl px-4 py-3 text-sm bg-[hsl(var(--danger-soft))] text-foreground">
                {error}
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(1)}>
                {t("wizard.back")}
              </Button>
              <Button
                variant="default"
                disabled={!canSubmit}
                onClick={() => create.mutate()}
              >
                {create.isPending ? t("wizard.creating") : t("wizard.createSession")}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-[hsl(var(--ok-soft))] text-foreground px-4 py-3 text-sm">
              {t("wizard.share.createdOk")}
            </div>
            {shareLink ? (
              <div className="space-y-2">
                <div className="space-y-1.5">
                  <Label>{t("wizard.share.link")}</Label>
                  <div className="flex gap-2">
                    <Input value={shareLink} readOnly className="text-sm" />
                    <Button variant="outline" onClick={copyShareLink}>
                      <Copy className="h-4 w-4" /> {t("wizard.share.copy")}
                    </Button>
                  </div>
                </div>
                <Button variant="default" className="w-full" onClick={copyWhatsApp}>
                  <MessageSquare className="h-4 w-4" />
                  {t("wizard.share.copyWhatsApp")}
                </Button>
              </div>
            ) : error ? (
              <div className="rounded-2xl px-4 py-3 text-sm bg-[hsl(var(--warn-soft))] text-foreground">
                {error}
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="default" onClick={() => onOpenChange(false)}>
                {t("wizard.share.close")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
